import { useState } from "react";

// Client-side sort + pagination for the admin list pages. Filtering stays in
// the page (it's domain-specific — status, shop, search); the page feeds the
// already-filtered rows in here and this owns ordering + paging only. Suited to
// the seeded, modest-sized admin lists; not a substitute for server paging on
// large datasets.

export type SortDir = "asc" | "desc";
export type SortState<K extends string> = { key: K; dir: SortDir };

type Cell = string | number | null | undefined;
type Accessor<T> = (row: T) => Cell;

export type DataTableOptions<T, K extends string> = {
	rows: T[];
	// One accessor per sortable column key — returns the value to order by.
	accessors: Record<K, Accessor<T>>;
	initialSort?: SortState<K>;
	pageSize?: number;
};

export type DataTable<T, K extends string> = {
	pageRows: T[];
	total: number;
	sort: SortState<K> | null;
	toggleSort: (key: K) => void;
	page: number;
	pageCount: number;
	pageSize: number;
	setPage: (n: number) => void;
	setPageSize: (n: number) => void;
	rangeStart: number;
	rangeEnd: number;
};

// Empty/null values sort last regardless of direction; numbers compare
// numerically, everything else by locale (ru — the default admin locale).
const compare = (a: Cell, b: Cell): number => {
	const ae = a == null || a === "";
	const be = b == null || b === "";
	if (ae && be) return 0;
	if (ae) return 1;
	if (be) return -1;
	if (typeof a === "number" && typeof b === "number") return a - b;
	return String(a).localeCompare(String(b), "ru");
};

export function useDataTable<T, K extends string>(
	opts: DataTableOptions<T, K>,
): DataTable<T, K> {
	const [sort, setSort] = useState<SortState<K> | null>(opts.initialSort ?? null);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSizeState] = useState(opts.pageSize ?? 10);

	const sorted = sort
		? [...opts.rows].sort((ra, rb) => {
				const acc = opts.accessors[sort.key];
				const r = compare(acc(ra), acc(rb));
				return sort.dir === "asc" ? r : -r;
			})
		: opts.rows;

	const total = sorted.length;
	const pageCount = Math.max(1, Math.ceil(total / pageSize));
	// Clamp for display so a shrinking filtered set never strands the view on an
	// empty page; the raw page state is harmless because we only read safePage.
	const safePage = Math.min(page, pageCount);
	const start = (safePage - 1) * pageSize;
	const pageRows = sorted.slice(start, start + pageSize);

	const toggleSort = (key: K) =>
		setSort((s) =>
			s?.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
		);

	const setPageSize = (n: number) => {
		setPageSizeState(n);
		setPage(1);
	};

	return {
		pageRows,
		total,
		sort,
		toggleSort,
		page: safePage,
		pageCount,
		pageSize,
		setPage,
		setPageSize,
		rangeStart: total === 0 ? 0 : start + 1,
		rangeEnd: Math.min(start + pageSize, total),
	};
}
